import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';

const AXIOM_LOGO = 'https://customer-assets.emergentagent.com/job_sponsor-bot-clinical/artifacts/kgiafx8p_AxiomLogo.png';

function TermsOfServicePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-zinc-100 pb-20">
      <header className="fixed top-0 left-0 right-0 bg-white border-b border-zinc-200 z-40 h-16 px-4 flex items-center justify-between">
        <button onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5 text-zinc-950" />
        </button>
        <img src={AXIOM_LOGO} alt="Axiom" className="h-8" />
        <div className="w-5" />
      </header>

      <main className="pt-20 px-4 pb-6">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl font-bold text-zinc-950 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Terms of Service</h1>
          <p className="text-sm text-zinc-500 mb-8">Last Updated: {new Date().toLocaleDateString()}</p>

          <div className="bg-white border border-zinc-200 rounded-md p-6 space-y-6">
            <section>
              <h2 className="text-xl font-bold text-zinc-950 mb-3">1. Acceptance of Terms</h2>
              <p className="text-sm text-zinc-700 leading-relaxed">
                By accessing and using the Axiom Recovery Programme ("Service"), you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using this Service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-zinc-950 mb-3">2. Programme Description</h2>
              <p className="text-sm text-zinc-700 leading-relaxed">
                Axiom is a 32-week rehabilitation programme (the 2-Week Window plus 30 Programme Weeks) designed for individuals in Recovery from alcohol and substance addiction. The programme includes:
              </p>
              <ul className="list-disc list-inside text-sm text-zinc-700 leading-relaxed space-y-1 mt-2">
                <li>2-Week Window (Weeks -2 to 0): Data collection period</li>
                <li>30 Weeks of structured rehabilitation</li>
                <li>Daily tasks: Journal entries, mental health check-ins</li>
                <li>Weekly tasks: 5 TSF meetings (up to 2 via Zoom)</li>
                <li>24/7 AI sponsor bot support</li>
                <li>Progress tracking and accountability reports</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-zinc-950 mb-3">3. User Responsibilities</h2>
              <p className="text-sm text-zinc-700 leading-relaxed mb-2">You agree to:</p>
              <ul className="list-disc list-inside text-sm text-zinc-700 leading-relaxed space-y-1">
                <li>Complete all assigned daily and weekly tasks</li>
                <li>Attend 5 TSF meetings per week (maximum 2 via Zoom)</li>
                <li>Provide accurate and truthful information</li>
                <li>Submit verification photos for in-person meetings</li>
                <li>Maintain your sobriety throughout the programme</li>
                <li>Progress through weeks only when tasks are completed</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-zinc-950 mb-3">4. Medical Disclaimer</h2>
              <p className="text-sm text-zinc-700 leading-relaxed bg-yellow-50 border border-yellow-200 p-4 rounded">
                <strong>IMPORTANT:</strong> This programme is an aid for Recovery and does NOT replace professional medical advice, diagnosis, treatment or supervised detoxification. If you are experiencing acute withdrawal symptoms or a medical emergency, contact emergency services immediately (911 or your local emergency number).
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-zinc-950 mb-3">5. Criminal Justice Compliance</h2>
              <p className="text-sm text-zinc-700 leading-relaxed">
                This programme may generate accountability reports for criminal justice systems. By using this Service, you acknowledge and consent to:
              </p>
              <ul className="list-disc list-inside text-sm text-zinc-700 leading-relaxed space-y-1 mt-2">
                <li>Collection of attendance and progress data</li>
                <li>Generation of unencrypted reports when legally required</li>
                <li>Sharing of compliance information with authorized agencies</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-zinc-950 mb-3">6. Payment Terms</h2>
              <p className="text-sm text-zinc-700 leading-relaxed">
                Programme fees:
              </p>
              <ul className="list-disc list-inside text-sm text-zinc-700 leading-relaxed space-y-1 mt-2">
                <li><strong>Weekly:</strong> AUD 24.70 a week. Lifetime access comes after 28 weekly payments.</li>
                <li><strong>Upfront:</strong> AUD 690 for lifetime access.</li>
              </ul>
              <p className="text-sm text-zinc-700 leading-relaxed mt-2">
                All prices are in Australian Dollars (AUD) regardless of your location. Payments are processed securely via Stripe or PayPal. No refunds after Week 1 completion.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-zinc-950 mb-3">7. Account Security</h2>
              <p className="text-sm text-zinc-700 leading-relaxed">
                You are responsible for maintaining the confidentiality of your account credentials (email, password and PIN). You agree to notify us immediately of any unauthorized access.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-zinc-950 mb-3">8. Acceptable Use Policy</h2>
              <p className="text-sm text-zinc-700 leading-relaxed mb-2">You must NOT:</p>
              <ul className="list-disc list-inside text-sm text-zinc-700 leading-relaxed space-y-1">
                <li>Glorify substance use in group chats</li>
                <li>Provide unsolicited advice (cross-talk)</li>
                <li>Share other users' personal information</li>
                <li>Manipulate attendance verification</li>
                <li>Use the Service for any unlawful purpose</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-zinc-950 mb-3">9. Intellectual Property</h2>
              <p className="text-sm text-zinc-700 leading-relaxed">
                All content, trademarks and materials on this Service are owned by Axiom Recovery Programme or its licensors. You may not reproduce, distribute or create derivative works without explicit written permission.
              </p>
            </section>

            <section data-testid="tos-copyright-section">
              <h2 className="text-xl font-bold text-zinc-950 mb-3">Copyright</h2>
              <p className="text-sm text-zinc-700 leading-relaxed">
                All content in the Axiom Recovery Programme, including lessons, readings, Self-Work questions, audio, images and games, is owned by Graeme Tudor and protected by copyright. You may use it only for your own Recovery while your account is active. You must not copy, print, record, photograph, share, sell or publish any part of it without written permission. Breaking this rule may lead to your account being closed and to legal action.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-zinc-950 mb-3">10. Limitation of Liability</h2>
              <p className="text-sm text-zinc-700 leading-relaxed">
                Axiom provides this Service \"as is\" without warranties of any kind. We are not liable for any direct, indirect, incidental or consequential damages arising from your use of the Service. Your use of the Service is at your own risk.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-zinc-950 mb-3">11. Termination</h2>
              <p className="text-sm text-zinc-700 leading-relaxed">
                We reserve the right to terminate or suspend your account at any time for violations of these Terms. Upon termination, your right to use the Service will immediately cease.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-zinc-950 mb-3">12. Contact Information</h2>
              <p className="text-sm text-zinc-700 leading-relaxed">
                For questions about these Terms:
                <br /><strong>Email:</strong> support@axiomtudor8.com
                <br /><strong>Privacy contact:</strong> graeme@tudor8.com
              </p>
            </section>
          </div>

          <Button
            onClick={() => navigate(-1)}
            className="mt-6 bg-zinc-950 text-white hover:bg-zinc-800 rounded-md h-12"
          >
            Back
          </Button>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}

export default TermsOfServicePage;
